import axios from "axios";
import {
  ArcgisEndpointConstant,
  IPointWeatherRepository,
  PointWeatherEntity,
  PointWeatherQuery,
  ResponseValueObject,
} from "@gishub/domain";

/** API de pronóstico de Open-Meteo (abierta, sin clave). */
const OPEN_METEO_FORECAST = "https://api.open-meteo.com/v1/forecast";

/** Subcapas de límite censal (departamento/provincia) para ubicar la coordenada. */
const LIMITE_DEPARTMENT_LAYER = ArcgisEndpointConstant.LIMITE_DEPARTAMENTO.sublayer;
const LIMITE_PROVINCE_LAYER = ArcgisEndpointConstant.LIMITE_PROVINCIAL.sublayer;
/** Campos de nombre del servicio de límites (única fuente: `LIMITE_FIELDS`). */
const DEP_NAME_FIELD = ArcgisEndpointConstant.LIMITE_FIELDS.DEPARTAMENTO.name;
const PROV_NAME_FIELD = ArcgisEndpointConstant.LIMITE_FIELDS.PROVINCIA.name;

/** Variables `current` solicitadas a Open-Meteo (una sola petición). */
const CURRENT_VARIABLES = [
  "temperature_2m",
  "relative_humidity_2m",
  "apparent_temperature",
  "is_day",
  "precipitation",
  "weather_code",
  "cloud_cover",
  "pressure_msl",
  "wind_speed_10m",
  "wind_direction_10m",
  "wind_gusts_10m",
  "uv_index",
].join(",");

/** Variables `daily` (extremos y probabilidad del día). */
const DAILY_VARIABLES = [
  "temperature_2m_max",
  "temperature_2m_min",
  "precipitation_probability_max",
  "sunrise",
  "sunset",
  "uv_index_max",
].join(",");

/**
 * Repositorio de meteorología puntual sobre Open-Meteo.
 *
 *  • Una petición por clic: `current` (estado actual) + `daily` (extremos del
 *    día) para la coordenada solicitada.
 *  • En paralelo, ubica la coordenada en los límites censales del MIDAGRI
 *    (SERV_LIMITE_CENSAL: departamento y provincia) mediante una intersección
 *    puntual. Si cae fuera del Perú, la ubicación queda en `null` y el popup
 *    muestra sólo las coordenadas.
 *
 * Ambas fuentes son públicas y CORS-friendly desde el navegador.
 */
export class OpenMeteoPointWeatherRepository
  implements IPointWeatherRepository
{
  async getPoint(
    query: PointWeatherQuery,
  ): Promise<ResponseValueObject<PointWeatherEntity>> {
    try {
      const [weather, place] = await Promise.all([
        this.fetchWeather(query),
        this.resolvePlace(query).catch(() => ({
          department: null,
          province: null,
        })),
      ]);

      return new ResponseValueObject(
        true,
        new PointWeatherEntity(
          query.longitude,
          query.latitude,
          weather.elevation,
          weather.timezone,
          weather.observedAt,
          weather.isDay,
          weather.temperature,
          weather.apparentTemperature,
          weather.humidity,
          weather.precipitation,
          weather.weatherCode,
          weather.cloudCover,
          weather.pressure,
          weather.windSpeed,
          weather.windDirection,
          weather.windGusts,
          weather.uvIndex,
          weather.temperatureMax,
          weather.temperatureMin,
          weather.precipitationProbabilityMax,
          weather.sunrise,
          weather.sunset,
          place.department,
          place.province,
        ),
      );
    } catch (error) {
      return new ResponseValueObject<PointWeatherEntity>(
        false,
        undefined,
        (error as Error)?.message ?? "No se pudo obtener el clima del punto",
      );
    }
  }

  // ── Meteorología (Open-Meteo) ─────────────────────────────────────

  private async fetchWeather(query: PointWeatherQuery): Promise<RawWeather> {
    const response = await axios.get(OPEN_METEO_FORECAST, {
      params: {
        latitude: query.latitude,
        longitude: query.longitude,
        current: CURRENT_VARIABLES,
        daily: DAILY_VARIABLES,
        timezone: "auto",
        forecast_days: 1,
      },
      timeout: 15000,
    });
    const data = response.data ?? {};
    const current = data.current ?? {};
    const daily = data.daily ?? {};
    return {
      elevation: this.num(data.elevation),
      timezone: data.timezone ?? null,
      observedAt: current.time ?? null,
      isDay: typeof current.is_day === "number" ? current.is_day === 1 : null,
      temperature: this.num(current.temperature_2m),
      apparentTemperature: this.num(current.apparent_temperature),
      humidity: this.num(current.relative_humidity_2m),
      precipitation: this.num(current.precipitation),
      weatherCode: this.num(current.weather_code),
      cloudCover: this.num(current.cloud_cover),
      pressure: this.num(current.pressure_msl),
      windSpeed: this.num(current.wind_speed_10m),
      windDirection: this.num(current.wind_direction_10m),
      windGusts: this.num(current.wind_gusts_10m),
      uvIndex: this.num(current.uv_index),
      temperatureMax: this.num(daily.temperature_2m_max?.[0]),
      temperatureMin: this.num(daily.temperature_2m_min?.[0]),
      precipitationProbabilityMax: this.num(
        daily.precipitation_probability_max?.[0],
      ),
      sunrise: daily.sunrise?.[0] ?? null,
      sunset: daily.sunset?.[0] ?? null,
    };
  }

  // ── Ubicación administrativa (límites censales MIDAGRI) ────────────

  /** Departamento y provincia que contienen la coordenada (o null si fuera del Perú). */
  private async resolvePlace(
    query: PointWeatherQuery,
  ): Promise<{ department: string | null; province: string | null }> {
    const geometry = JSON.stringify({
      x: query.longitude,
      y: query.latitude,
      spatialReference: { wkid: 4326 },
    });
    const [department, province] = await Promise.all([
      this.limitePointName(LIMITE_DEPARTMENT_LAYER, DEP_NAME_FIELD, geometry),
      this.limitePointName(LIMITE_PROVINCE_LAYER, PROV_NAME_FIELD, geometry),
    ]);
    return { department, province };
  }

  private async limitePointName(
    layerId: number,
    nameField: string,
    geometry: string,
  ): Promise<string | null> {
    const params = new URLSearchParams({
      where: "1=1",
      outFields: nameField,
      returnGeometry: "false",
      geometry,
      geometryType: "esriGeometryPoint",
      spatialRel: "esriSpatialRelIntersects",
      inSR: "4326",
      f: "json",
    });
    const response = await axios.post(
      `${ArcgisEndpointConstant.LIMITE_CENSAL_MAPSERVER}/${layerId}/query`,
      params,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        timeout: 12000,
      },
    );
    const name = response.data?.features?.[0]?.attributes?.[nameField];
    return name == null ? null : String(name);
  }

  private num(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  }
}

/** Forma cruda de la respuesta de Open-Meteo antes de mapear a la entidad. */
interface RawWeather {
  elevation: number | null;
  timezone: string | null;
  observedAt: string | null;
  isDay: boolean | null;
  temperature: number | null;
  apparentTemperature: number | null;
  humidity: number | null;
  precipitation: number | null;
  weatherCode: number | null;
  cloudCover: number | null;
  pressure: number | null;
  windSpeed: number | null;
  windDirection: number | null;
  windGusts: number | null;
  uvIndex: number | null;
  temperatureMax: number | null;
  temperatureMin: number | null;
  precipitationProbabilityMax: number | null;
  sunrise: string | null;
  sunset: string | null;
}
